import React, { useEffect } from "react";
import FuturesLeverageSlider from "../futures_trade/FuturesLeverageSlider";

/**
 * Cross margin — “Adjust Leverage” popup.
 * Uses cross_allowed_leverages from pair config (e.g. [3, 5] only — no filled gaps).
 */
export default function MarginLeverageModal({
  open,
  onClose,
  onConfirm,
  coinLabel,
  coinIconSrc,
  leverage,
  onChangeLeverage,
  allowedLeverages,
  minLeverage = 3,
  maxLeverage = 5,
  netEquity = 0,
  quoteSymbol = "",
  formatTotal,
}) {
  const fmt = (n) => (formatTotal ? formatTotal(n) : Number(n).toFixed(2));

  const allowedStr = Array.isArray(allowedLeverages) ? allowedLeverages.join(",") : "";

  const allowed = React.useMemo(() => {
    if (Array.isArray(allowedLeverages) && allowedLeverages.length > 0) {
      const nums = [...new Set(allowedLeverages.map(Number).filter((n) => Number.isFinite(n) && n > 0))]
        .sort((a, b) => a - b);
      if (nums.length > 0) return nums;
    }
    const min = Math.max(1, Math.round(Number(minLeverage) || 1));
    const max = Math.max(min, Math.round(Number(maxLeverage) || min));
    const arr = [];
    for (let i = min; i <= max; i += 1) arr.push(i);
    return arr;
  }, [allowedStr, allowedLeverages, minLeverage, maxLeverage]);

  const min = allowed[0];
  const max = allowed[allowed.length - 1];

  const clamp = (n) => {
    const x = Number(n);
    if (!allowed.length) return minLeverage;
    if (!Number.isFinite(x)) return allowed[0];
    if (allowed.includes(Math.round(x))) return Math.round(x);
    return allowed.reduce((best, v) => (Math.abs(v - x) < Math.abs(best - x) ? v : best), allowed[0]);
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onClose?.();
  };

  const handleConfirm = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const finalVal = clamp(leverage);
    onConfirm?.(finalVal);
    onClose?.();
  };

  useEffect(() => {
    if (!open || !allowed.length) return;
    if (!allowed.includes(leverage)) {
      onChangeLeverage?.(clamp(leverage));
    }
  }, [open, allowed, leverage, onChangeLeverage]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const current = clamp(leverage);
  const maxOpen = fmt(netEquity * current);

  return (
    <div className="agce-margin-lvg-modal-root" role="presentation">
      <div className="agce-margin-lvg-modal-backdrop" onClick={handleClose} aria-hidden />
      <div
        className="agce-margin-lvg-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="agce-margin-lvg-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="agce-margin-lvg-modal-head">
          <h2 id="agce-margin-lvg-title" className="agce-margin-lvg-modal-title">
            Adjust Leverage
          </h2>
          <button type="button" className="agce-margin-lvg-modal-close" onClick={handleClose} aria-label="Close">
            <i className="ri-close-line ri-lg" aria-hidden />
          </button>
        </div>
        <div className="agce-margin-lvg-modal-body">
          <span className="agce-margin-lvg-field-label">Pair</span>
          <div className="agce-margin-lvg-coin-row">
            {coinIconSrc ? <img src={coinIconSrc} alt="" width={24} height={24} /> : null}
            <span>{coinLabel || "—/—"}</span>
          </div>

          <span className="agce-margin-lvg-field-label">Leverage</span>
          <FuturesLeverageSlider
            value={current}
            onChange={onChangeLeverage}
            minLeverage={min}
            maxLeverage={max}
            allowedValues={allowed}
          />

          <ul className="agce-margin-lvg-notes">
            <li>
              Maximum position at current leverage: {maxOpen} {quoteSymbol}
            </li>
            <li>
              Please note that leverage changing will also apply for open positions and open orders.
            </li>
            <li>
              Selecting higher leverage increases your liquidation risk.
              Always manage your risk levels.
            </li>
          </ul>

          {netEquity <= 0 && (
            <p className="agce-margin-lvg-warn">
              The current available margin ≤ 0. You can increase the leverage or add margin.
            </p>
          )}
        </div>
        <div className="agce-margin-lvg-modal-foot">
          <button type="button" className="agce-margin-lvg-btn agce-margin-lvg-btn--ghost" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className="agce-margin-lvg-btn agce-margin-lvg-btn--primary"
            onClick={handleConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
